import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { formatPrice as fmtPrice } from '@/lib/utils';
import { readAccessToken } from "@/lib/authSession";
import {
  applyDocumentTheme,
  persistThemePreference,
  readThemePreference,
  resolveIsDark,
  type ThemePreference,
} from "@/lib/themeBootstrap";
import { ASSIGNED_WORKSPACES_KEY, WORKSPACE_STORAGE_KEY } from '@/lib/admin/workspaceRoutingFlag';
import { applyCountryRuntimePayload, fetchCountryRuntime } from '@/lib/trceConsumerBootstrap';
import { applyTrceThemePack } from '@/lib/trceRuntimeTheme';
import type { TrceFormsSlice, TrceFormSchema } from '@/lib/trceFormTypes';
import type { TrceAiContextSlice, TrceDashboardSlice } from '@/lib/trceAiContextTypes';
import type { TrcePromotionSlice, TrceExperimentSlice, TrceRecommendationSlice } from '@/lib/trcePhase5Types';
import type { TrceWhiteLabelSlice } from '@/lib/trceWhiteLabelTypes';
import { applyWhiteLabelDocumentBranding } from '@/lib/trceWhiteLabelTypes';
import { isAiFeatureAllowed } from '@/lib/trceAiContextTypes';
import { isWave1CustomerNavVisible, isWave1SurfaceAllowed } from '@/lib/wave1LaunchContract';

export type Language = 'dari' | 'pashto' | 'english' | 'arabic' | 'uzbek' | 'russian';
export type HomepageDisplayMode = 'focused' | 'grid' | 'tabs' | 'scroll_row' | 'sections';
export type VehicleCompanyType = 'dealer' | 'rental';
export type VehicleCompanyStatus = 'pending' | 'approved' | 'rejected';
type Role = 'customer' | 'vendor' | 'courier' | 'admin' | 'vehicle_company' | 'warehouse_operator' | 'agent';

/* ══════════════════════════════════════════════════════════════════
   ENTERPRISE WORKSPACE SYSTEM
   ══════════════════════════════════════════════════════════════════ */

export type WorkspaceModuleType =
  | 'customer'
  | 'vendor'
  | 'mart_vendor'
  | 'mart_operator'
  | 'courier'
  | 'agent'
  | 'vehicle_rental'
  | 'real_estate'
  | 'admin'
  | 'warehouse';

export type WorkspaceStatus = 'active' | 'pending' | 'apply';
export type PermissionLevel = 'full' | 'standard' | 'limited' | 'view_only';
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'reject' | 'assign' | 'refund' | 'settle' | 'export';

export interface WorkspaceLocation {
  countryCode: string;
  countryFlag: string;
  countryName: string;
  countryNameEn: string;
  city?: string;
  zone?: string;
  warehouse?: string;
}

export interface WorkspacePermission {
  level: PermissionLevel;
  actions: PermissionAction[];
}

export interface Workspace {
  id: string;
  moduleType: WorkspaceModuleType;
  icon: string;
  title: string;
  titleEn: string;
  roleLabel: string;
  roleLabelEn: string;
  location: WorkspaceLocation;
  permission: WorkspacePermission;
  status: WorkspaceStatus;
  isFavorite: boolean;
  lastUsed?: string;
  href: string;
  color: string;
  bgColor: string;
}

/** Build the default demo workspaces for the current country */
function buildDefaultWorkspaces(countryCode: string): Workspace[] {
  const isAF = countryCode === 'AF';
  const isOM = countryCode === 'OM';
  const flag = isAF ? '🇦🇫' : isOM ? '🇴🇲' : '🏳';
  const cName = isAF ? 'افغانستان' : isOM ? 'عُمان' : countryCode;
  const cNameEn = isAF ? 'Afghanistan' : isOM ? 'Oman' : countryCode;
  const city = isAF ? 'کابل' : isOM ? 'مسقط' : '';
  const cityEn = isAF ? 'Kabul' : isOM ? 'Muscat' : '';

  const loc = (warehouse?: string, zone?: string): WorkspaceLocation => ({
    countryCode, countryFlag: flag, countryName: cName, countryNameEn: cNameEn,
    city: cityEn, zone, warehouse,
  });

  return [
    {
      id: 'ws-customer',
      moduleType: 'customer',
      icon: '🛒',
      title: 'مشتری',
      titleEn: 'Customer',
      roleLabel: 'مشتری',
      roleLabelEn: 'Customer',
      location: loc(),
      permission: { level: 'standard', actions: ['view', 'create'] },
      status: 'active',
      isFavorite: true,
      lastUsed: new Date().toISOString(),
      href: '/',
      color: '#059669',
      bgColor: '#ECFDF5',
    },
    {
      id: 'ws-vendor',
      moduleType: 'vendor',
      icon: '🏪',
      title: 'پنل فروشنده',
      titleEn: 'Vendor Panel',
      roleLabel: 'فروشنده',
      roleLabelEn: 'Vendor',
      location: loc(),
      permission: { level: 'standard', actions: ['view', 'create', 'edit', 'export'] },
      status: 'apply',
      isFavorite: false,
      href: '/vendor',
      color: '#7C3AED',
      bgColor: '#F5F3FF',
    },
    {
      id: 'ws-mart-vendor',
      moduleType: 'mart_vendor',
      icon: '🛍️',
      title: 'تسلط مارت — فروشنده',
      titleEn: 'Tasallot Mart — Vendor',
      roleLabel: 'فروشنده مارت',
      roleLabelEn: 'Mart Vendor',
      location: loc(),
      permission: { level: 'standard', actions: ['view', 'create', 'edit', 'export'] },
      status: 'apply',
      isFavorite: false,
      href: '/mart/vendor',
      color: '#059669',
      bgColor: '#ECFDF5',
    },
    {
      id: 'ws-mart-operator',
      moduleType: 'mart_operator',
      icon: '📦',
      title: 'تسلط مارت — انبار',
      titleEn: 'Tasallot Mart — Warehouse',
      roleLabel: 'اپراتور انبار',
      roleLabelEn: 'Warehouse Operator',
      location: loc('WH-02', 'ناحیه ۳'),
      permission: { level: 'limited', actions: ['view', 'edit', 'approve'] },
      status: 'apply',
      isFavorite: false,
      href: '/mart/warehouse',
      color: '#b45309',
      bgColor: '#FFFBEB',
    },
    {
      id: 'ws-courier',
      moduleType: 'courier',
      icon: '🚚',
      title: 'داشبورد پیک',
      titleEn: 'Courier Dashboard',
      roleLabel: 'پیک / کوریر',
      roleLabelEn: 'Courier',
      location: loc(),
      permission: { level: 'limited', actions: ['view', 'edit', 'assign'] },
      status: 'apply',
      isFavorite: false,
      href: '/courier',
      color: '#F59E0B',
      bgColor: '#FFFBEB',
    },
    {
      id: 'ws-admin',
      moduleType: 'admin',
      icon: '⚙️',
      title: 'پنل ادمین',
      titleEn: 'Admin Panel',
      roleLabel: 'ادمین',
      roleLabelEn: 'Admin',
      location: { countryCode: 'GLOBAL', countryFlag: '🌐', countryName: 'جهانی', countryNameEn: 'Global' },
      permission: { level: 'full', actions: ['view', 'create', 'edit', 'delete', 'approve', 'reject', 'assign', 'refund', 'settle', 'export'] },
      status: 'apply',
      isFavorite: false,
      href: '/admin',
      color: '#DC2626',
      bgColor: '#FEF2F2',
    },
    {
      id: 'ws-agent',
      moduleType: 'agent',
      icon: '🏬',
      title: 'داشبورد نماینده',
      titleEn: 'Agent Dashboard',
      roleLabel: 'نماینده',
      roleLabelEn: 'Agent',
      location: loc(),
      permission: { level: 'standard', actions: ['view', 'create', 'export'] },
      status: 'apply',
      isFavorite: false,
      href: '/agent',
      color: '#0D9488',
      bgColor: '#F0FDFA',
    },
    {
      id: 'ws-vehicle',
      moduleType: 'vehicle_rental',
      icon: '🚗',
      title: 'کمپنی وسایط نقلیه',
      titleEn: 'Vehicle Company',
      roleLabel: 'ادمین کمپنی',
      roleLabelEn: 'Company Admin',
      location: loc(),
      permission: { level: 'standard', actions: ['view', 'create', 'edit', 'export'] },
      status: 'apply',
      isFavorite: false,
      href: '/vehicle-company',
      color: '#2563EB',
      bgColor: '#EFF6FF',
    },
    {
      id: 'ws-realestate',
      moduleType: 'real_estate',
      icon: '🏠',
      title: 'بازار ملک',
      titleEn: 'Real Estate Portal',
      roleLabel: 'نماینده ملک',
      roleLabelEn: 'Property Agent',
      location: loc(),
      permission: { level: 'standard', actions: ['view', 'create', 'edit'] },
      status: 'apply',
      isFavorite: false,
      href: '/real-estate',
      color: '#10B981',
      bgColor: '#ECFDF5',
    },
  ];
}

/** Map DB language_code → app Language identifier */
const LANG_CODE_MAP: Record<string, Language> = {
  fa: 'dari',
  ps: 'pashto',
  en: 'english',
  ar: 'arabic',
  uz: 'uzbek',
  ru: 'russian',
};

/** Fallback languages per country when the bootstrap API returns no language data */
const COUNTRY_FALLBACK_LANGUAGES: Record<string, SupportedLanguage[]> = {
  AF: [
    { code: 'fa', appKey: 'dari',    name: 'Dari',    native: 'دری',      isRtl: true,  isDefault: true  },
    { code: 'ps', appKey: 'pashto',  name: 'Pashto',  native: 'پښتو',    isRtl: true,  isDefault: false },
    { code: 'en', appKey: 'english', name: 'English', native: 'English',  isRtl: false, isDefault: false },
  ],
  OM: [
    { code: 'ar', appKey: 'arabic',  name: 'Arabic',  native: 'العربية', isRtl: true,  isDefault: true  },
    { code: 'en', appKey: 'english', name: 'English', native: 'English',  isRtl: false, isDefault: false },
  ],
};

/** RTL languages */
const RTL_LANGS = new Set<Language>(['dari', 'pashto', 'arabic']);

/** Arabic display names for each module (fallback when language = arabic) */
const ARABIC_MODULE_NAMES: Record<string, string> = {
  marketplace:  'السوق',
  real_estate:  'العقارات',
  food:         'الطعام والمطاعم',
  vehicles:     'المركبات',
  classifieds:  'الإعلانات المبوبة',
  courier:      'خدمات الشحن',
  grocery:      'البقالة',
};

/** Fallback country config used when API returns incomplete data */
const FALLBACK_CURRENCY: Record<string, { code: string; symbol: string }> = {
  AF: { code: 'AFN', symbol: '؋' },
  OM: { code: 'OMR', symbol: 'ر.ع.' },
  UZ: { code: 'UZS', symbol: "so'm" },
  AE: { code: 'AED', symbol: 'د.إ' },
  PK: { code: 'PKR', symbol: '₨' },
  SA: { code: 'SAR', symbol: 'ر.س' },
  IR: { code: 'IRR', symbol: '﷼' },
};

export interface AppModule {
  id: number;
  name: string;
  displayNameDari: string;
  displayName: string;
  icon: string;
  href: string;
  status: 'hidden' | 'internal_ready' | 'soft_launch' | 'focused_live' | 'stable_live' | 'paused';
  isVisibleInNav: boolean;
  isHomepageFocus: boolean;
  isSearchable: boolean;
  isPostingEnabled: boolean;
  isCheckoutEnabled: boolean;
  isMarketingEnabled: boolean;
  isPushEnabled: boolean;
  displayPriority: number;
  enabledCities: string[];
  heroConfig?: Record<string, any>;
}

export interface SupportedLanguage {
  code: string;
  appKey: Language;
  name: string;
  native: string;
  isRtl: boolean;
  isDefault: boolean;
}

export interface CountrySummary {
  id: number;
  code: string;
  nameDari: string;
  nameEn: string;
  flagIcon: string;
  status: string;
  defaultCurrency?: string;
  currencySymbol?: string;
}

export interface CountryCity {
  id: number;
  nameEn: string;
  nameDari: string;
  nameAr?: string | null;
  province?: string | null;
  countryCode: string;
  isActive: boolean;
}

export interface CountryZone {
  id: number;
  cityId: number;
  nameEn: string;
  nameDari: string;
  nameAr?: string | null;
  code: string;
  isActive: boolean;
  sortOrder: number;
}

export interface CountryPaymentMethod {
  id: number;
  countryId: number;
  key: string;
  labelFa: string;
  labelEn: string;
  labelAr: string;
  labelUz: string;
  descriptionFa: string;
  descriptionEn: string;
  descriptionAr: string;
  descriptionUz: string;
  icon: string;
  iconColor: string;
  isActive: boolean;
  isComingSoon: boolean;
  displayOrder: number;
}

export interface SavedAddress {
  id: number;
  nickname: string;
  city: string;
  street: string;
  landmark: string;
  phone: string;
  name: string;
  lat: number | null;
  lng: number | null;
  isDefault: boolean;
}

export interface UserProfile {
  name: string;
  phone: string;
  email: string;
  city: string;
  adminWorkspaces?: string[];
  adminWorkspace?: string;
}

interface AppState {
  role: Role;
  language: Language;
  city: string;
  setRole: (role: Role) => void;
  setLanguage: (lang: Language) => void;
  setCity: (city: string) => void;
  vehicleCompanyType: VehicleCompanyType;
  vehicleCompanyStatus: VehicleCompanyStatus;
  setVehicleCompanyType: (t: VehicleCompanyType) => void;
  setVehicleCompanyStatus: (s: VehicleCompanyStatus) => void;
  cartCount: number;
  setCartCount: React.Dispatch<React.SetStateAction<number>>;
  cart: any[];
  setCart: React.Dispatch<React.SetStateAction<any[]>>;
  modules: AppModule[];
  modulesLoaded: boolean;
  isModuleVisible: (name: string) => boolean;
  isModuleInNav: (name: string) => boolean;
  getModuleName: (mod: AppModule) => string;
  isEn: boolean;
  isAr: boolean;
  isRtl: boolean;
  t: (labels: { dari: string; en: string; ar?: string; ps?: string; uz?: string }) => string;
  homepageDisplayMode: HomepageDisplayMode;
  supportedLanguages: SupportedLanguage[];
  countryCities: CountryCity[];
  selectedCityId: number | null;
  setSelectedCityId: (id: number | null) => void;
  countryZones: CountryZone[];
  selectedZoneId: number | null;
  setSelectedZoneId: (id: number | null) => void;
  checkModuleAvailable: (moduleKey: string, cityId?: number, zoneId?: number) => Promise<boolean>;
  deploymentCountryCode: string;
  selectedCountryCode: string;
  setSelectedCountryCode: (code: string) => void;
  availableCountries: CountrySummary[];
  isGeneralAdmin: boolean;
  adminCountryId: number | null;
  /** Admin tier: 'global' | 'country' | 'province' | 'module' | 'department' | null */
  adminTier: string | null;
  /** Scope value for the tier, e.g. province code "KBL", module key "taxi", department "cs" */
  adminScopeValue: string | null;
  isLoggedIn: boolean;
  currentUserId: number | null;
  /** Raw JWT string for authenticated API calls (null if not logged in) */
  token: string | null;
  /** Current user object (id + email) if logged in */
  user: { id: number; email?: string } | null;
  countryBranding: { primaryColor?: string; appName?: string; appNameEn?: string; flagIcon?: string } | null;
  /** Re-fetch available countries list (e.g. after admin changes country status) */
  refreshCountries: () => void;
  /** ISO currency code for the active country, e.g. "AFN", "OMR" */
  currencyCode: string;
  /** Currency symbol, e.g. "؋", "ر.ع." */
  currencySymbol: string;
  /** Format a price amount using the current country's currency (optional override with currencyCode) */
  formatPrice: (amount: number | string | null | undefined, currencyOverride?: string) => string;
  /** Measurement unit: "km" or "miles" */
  measurementUnit: 'km' | 'miles';
  /** Country phone prefix, e.g. "+93" */
  phonePrefix: string;
  /** Active country flag emoji */
  countryFlag: string;
  /** Active country name in current language */
  countryName: string;
  /** Payment methods available in the active country (from DB) */
  paymentMethods: CountryPaymentMethod[];
  /** Feature flags per module from DB, e.g. moduleFeatureFlags.marketplace.delivery_tiers */
  moduleFeatureFlags: Record<string, Record<string, string>>;
  /** TRCE L4 — runtime form schemas per country (from /runtime/resolve) */
  trceForms: TrceFormsSlice | null;
  /** Resolve a TRCE form schema by module + use-case */
  getTrceForm: (moduleKey: string, useCase: string) => TrceFormSchema | null;
  /** TRCE L9 — AI context bundle for current country/persona */
  trceAiContext: TrceAiContextSlice | null;
  /** TRCE L8 — admin dashboard widget layout (when resolved via TRCE) */
  trceDashboard: TrceDashboardSlice | null;
  /** TRCE L10 — calendar promotions + live banners */
  trcePromotions: TrcePromotionSlice | null;
  /** TRCE L11 — unified experiments (rollout + hero A/B) */
  trceExperiments: TrceExperimentSlice | null;
  /** TRCE L12 — home module order + shortcuts */
  trceRecommendations: TrceRecommendationSlice | null;
  /** TRCE L15 — white-label tenant orchestration */
  trceWhiteLabel: TrceWhiteLabelSlice | null;
  /** Check if an AI feature is allowed in current TRCE AI context */
  isTrceAiFeatureAllowed: (featureKey: string) => boolean;
  /** Resolved dark class (system preference applied when themePreference=system). */
  isDarkMode: boolean;
  /** User preference: system | light | dark (default system). */
  themePreference: ThemePreference;
  setThemePreference: (pref: ThemePreference) => void;
  /** Cycles light → dark → system for compact toggles; prefer setThemePreference in Settings. */
  toggleDarkMode: () => void;
  /* ── ENTERPRISE WORKSPACE SYSTEM ── */
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  setActiveWorkspace: (ws: Workspace) => void;
  toggleWorkspaceFavorite: (wsId: string) => void;
  requestWorkspaceAccess: (moduleType: WorkspaceModuleType) => void;
  savedAddresses: SavedAddress[];
  setSavedAddresses: React.Dispatch<React.SetStateAction<SavedAddress[]>>;
  userProfile: UserProfile;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
}

const AppContext = createContext<AppState | undefined>(undefined);

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

function syncWorkspaceFromToken(payload: Record<string, any> | null): void {
  if (!payload) return;
  const workspaces = Array.isArray(payload.adminWorkspaces)
    ? payload.adminWorkspaces.filter((w: unknown) => typeof w === 'string')
    : [];
  if (workspaces.length > 0) {
    try {
      localStorage.setItem(ASSIGNED_WORKSPACES_KEY, workspaces.join(','));
      const primary = payload.adminWorkspace;
      if (typeof primary === 'string' && workspaces.includes(primary)) {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, primary);
      }
    } catch { /* ignore */ }
  }
}

function decodeJwt(token: string): Record<string, any> | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

/**
 * Determines the country for this deployment.
 * Priority: VITE_COUNTRY_CODE → hostname pattern → default AF
 */
function getDeploymentCountry(): string {
  const env = import.meta.env.VITE_COUNTRY_CODE;
  if (env && /^[A-Z]{2}$/.test(env)) return env;
  const h = window.location.hostname.toLowerCase();
  if (h.startsWith('uz.') || h.includes('.uz.') || h.endsWith('.uz')) return 'UZ';
  if (h.startsWith('ir.') || h.includes('.ir.') || h.endsWith('.ir')) return 'IR';
  if (h.startsWith('pk.') || h.includes('.pk.') || h.endsWith('.pk')) return 'PK';
  if (h.startsWith('om.') || h.includes('.om.') || h.endsWith('.om')) return 'OM';
  return 'AF';
}

const DEPLOYMENT_COUNTRY = getDeploymentCountry();

/** Extra per-country config not always in DB */
const PHONE_PREFIXES: Record<string, string> = {
  AF: '+93', OM: '+968', UZ: '+998', AE: '+971', PK: '+92', SA: '+966', IR: '+98',
};

function isSuperAdminPayload(p: Record<string, any> | null | undefined): boolean {
  if (!p) return false;
  return !!(
    p.isGeneralAdmin
    || p.role === 'super_admin'
    || p.role === 'global_admin'
    || p.adminTier === 'global'
    || p.admin_tier === 'global'
  );
}

function readTokenPayload(): Record<string, any> | null {
  const token = readAccessToken();
  if (!token) return null;
  return decodeJwt(token);
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [role, setRole] = useState<Role>(() => {
    const p = readTokenPayload();
    if (p?.role === 'admin' || p?.role === 'super_admin' || p?.role === 'global_admin' || isSuperAdminPayload(p)) return 'admin' as Role;
    return 'customer';
  });
  const LANG_STORAGE_KEY = 'tasallot_language';
  const VALID_LANGS: Language[] = ['dari', 'pashto', 'english', 'arabic', 'uzbek', 'russian'];
  const _urlLang = new URLSearchParams(window.location.search).get('lang') as Language | null;
  const [language, setLanguageState] = useState<Language>(() => {
    if (_urlLang && VALID_LANGS.includes(_urlLang)) return _urlLang;
    const saved = localStorage.getItem(LANG_STORAGE_KEY) as Language | null;
    return saved && VALID_LANGS.includes(saved) ? saved : 'dari';
  });
  // After the first bootstrap, language is locked — country changes should NOT auto-switch it.
  // URL param or user choice both lock it; only first visit without either uses the country default.
  const initialLangSet = useRef(
    !!localStorage.getItem(LANG_STORAGE_KEY) ||
    !!(_urlLang && VALID_LANGS.includes(_urlLang))
  );
  const setLanguage = useCallback((lang: Language) => {
    // Persist so page refresh keeps the user's chosen language
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    // Mark permanently so future country switches won't override
    initialLangSet.current = true;
    setLanguageState(lang);
  }, []);
  const [city, setCity] = useState('Kabul');
  const [vehicleCompanyType, setVehicleCompanyType] = useState<VehicleCompanyType>('dealer');
  const [vehicleCompanyStatus, setVehicleCompanyStatus] = useState<VehicleCompanyStatus>('pending');
  const [cartCount, setCartCount] = useState(0);
  const [cart, setCart] = useState<any[]>([]);

  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>(() => {
    try { return JSON.parse(localStorage.getItem('tasallot_addresses') ?? '[]'); } catch { return []; }
  });
  const [userProfile, setUserProfile] = useState<UserProfile>(() => {
    try { return JSON.parse(localStorage.getItem('tasallot_user_profile') ?? 'null') ?? { name: "احمد خان", phone: "0799 123 456", email: "ahmad@email.com", city: "کابل" }; } catch { return { name: "احمد خان", phone: "0799 123 456", email: "ahmad@email.com", city: "کابل" }; }
  });

  const [modules, setModules] = useState<AppModule[]>([]);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [homepageDisplayMode, setHomepageDisplayMode] = useState<HomepageDisplayMode>('focused');
  const [supportedLanguages, setSupportedLanguages] = useState<SupportedLanguage[]>([]);
  const [selectedCountryCode, setSelectedCountryCodeState] = useState(() => {
    try {
      return localStorage.getItem('tasallot_country_code') ?? DEPLOYMENT_COUNTRY;
    } catch {
      return DEPLOYMENT_COUNTRY;
    }
  });
  const [availableCountries, setAvailableCountries] = useState<CountrySummary[]>([]);
  const [isGeneralAdmin, setIsGeneralAdmin] = useState<boolean>(() => isSuperAdminPayload(readTokenPayload()));
  const [adminCountryId, setAdminCountryId] = useState<number | null>(() => {
    const p = readTokenPayload();
    return p?.countryId != null ? Number(p.countryId) : null;
  });
  const [adminTier, setAdminTier] = useState<string | null>(() => {
    const p = readTokenPayload() as any;
    return p?.adminTier ?? null;
  });
  const [adminScopeValue, setAdminScopeValue] = useState<string | null>(() => {
    const p = readTokenPayload() as any;
    return p?.adminScopeValue ?? null;
  });
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => !!readAccessToken());
  const [currentUserId, setCurrentUserId] = useState<number | null>(() => {
    const p = readTokenPayload();
    if (!p) return null;
    return p?.userId ? Number(p.userId) : (p?.sub ? Number(p.sub) : (p?.id ? Number(p.id) : null));
  });

  /* ── WORKSPACE STATE ── */
  const [workspaces, setWorkspaces] = useState<Workspace[]>(() => buildDefaultWorkspaces(DEPLOYMENT_COUNTRY));
  const [activeWorkspace, setActiveWorkspaceState] = useState<Workspace | null>(
    () => buildDefaultWorkspaces(DEPLOYMENT_COUNTRY).find(w => w.id === 'ws-customer') ?? null
  );
  const [countryBranding, setCountryBranding] = useState<AppState['countryBranding']>(null);
  const [countryCities, setCountryCities] = useState<CountryCity[]>([]);
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [countryZones, setCountryZones] = useState<CountryZone[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [currencyCode, setCurrencyCode] = useState<string>('AFN');
  const [currencySymbol, setCurrencySymbol] = useState<string>('؋');
  const [countryFlag, setCountryFlag] = useState<string>('🇦🇫');
  const [countryName, setCountryName] = useState<string>('افغانستان');
  const [measurementUnit] = useState<'km' | 'miles'>('km');
  const [paymentMethods, setPaymentMethods] = useState<CountryPaymentMethod[]>([]);
  const [moduleFeatureFlags, setModuleFeatureFlags] = useState<Record<string, Record<string, string>>>({});
  const [trceForms, setTrceForms] = useState<TrceFormsSlice | null>(null);
  const [trceAiContext, setTrceAiContext] = useState<TrceAiContextSlice | null>(null);
  const [trceDashboard, setTrceDashboard] = useState<TrceDashboardSlice | null>(null);
  const [trcePromotions, setTrcePromotions] = useState<TrcePromotionSlice | null>(null);
  const [trceExperiments, setTrceExperiments] = useState<TrceExperimentSlice | null>(null);
  const [trceRecommendations, setTrceRecommendations] = useState<TrceRecommendationSlice | null>(null);
  const [trceWhiteLabel, setTrceWhiteLabel] = useState<TrceWhiteLabelSlice | null>(null);

  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => readThemePreference());
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => resolveIsDark(readThemePreference()));

  useEffect(() => {
    const apply = () => {
      const resolved = resolveIsDark(themePreference);
      setIsDarkMode(resolved);
      applyDocumentTheme(resolved);
      persistThemePreference(themePreference, resolved);
    };
    apply();
    if (themePreference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [themePreference]);

  const setThemePreference = (pref: ThemePreference) => setThemePreferenceState(pref);

  const toggleDarkMode = () =>
    setThemePreferenceState((prev) => {
      if (prev === "light") return "dark";
      if (prev === "dark") return "system";
      return "light";
    });

  const isRtl = RTL_LANGS.has(language);

  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang =
      language === 'english' ? 'en' :
      language === 'arabic'  ? 'ar' :
      language === 'pashto'  ? 'ps' :
      language === 'uzbek'   ? 'uz' :
      language === 'russian' ? 'ru' : 'fa-AF';
  }, [language, isRtl]);

  useEffect(() => {
    localStorage.setItem('tasallot_addresses', JSON.stringify(savedAddresses));
  }, [savedAddresses]);

  useEffect(() => {
    localStorage.setItem('tasallot_user_profile', JSON.stringify(userProfile));
  }, [userProfile]);

  useEffect(() => {
    const checkToken = () => {
      const token = readAccessToken();
      if (token) {
        const payload = decodeJwt(token);
        setIsGeneralAdmin(isSuperAdminPayload(payload));
        setAdminCountryId(payload?.countryId != null ? Number(payload.countryId) : null);
        setAdminTier((payload as any)?.adminTier ?? (payload as any)?.admin_tier ?? null);
        setAdminScopeValue((payload as any)?.adminScopeValue ?? null);
        syncWorkspaceFromToken(payload);
        if (payload?.role === 'admin') setRole('admin' as Role);
        const uid = payload?.userId ? Number(payload.userId) : (payload?.sub ? Number(payload.sub) : (payload?.id ? Number(payload.id) : null));
        setCurrentUserId(uid);
        setIsLoggedIn(true);
      } else {
        setIsGeneralAdmin(false);
        setAdminCountryId(null);
        setAdminTier(null);
        setAdminScopeValue(null);
        setCurrentUserId(null);
        setIsLoggedIn(false);
        setRole('customer' as Role);
      }
    };
    checkToken();
    window.addEventListener('tasallot_token_changed', checkToken);
    return () => window.removeEventListener('tasallot_token_changed', checkToken);
  }, []);

  /* ORDER031-R3B: hydrate address book from server (localStorage = cache / migration source only) */
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const { syncCustomerAddressesFromServer, toLegacySavedAddress } = await import("@/lib/customerAddressApi");
        let local: unknown[] = [];
        try {
          local = JSON.parse(localStorage.getItem("tasallot_addresses") ?? "[]");
        } catch {
          local = [];
        }
        const server = await syncCustomerAddressesFromServer(local);
        if (!cancelled && Array.isArray(server)) {
          setSavedAddresses(server.map(toLegacySavedAddress));
        }
      } catch {
        /* offline / unauth — keep cache */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, currentUserId]);

  const getTrceForm = useCallback((moduleKey: string, useCase: string): TrceFormSchema | null => {
    const key = `${moduleKey}.${useCase}`;
    return trceForms?.resolved[key] ?? null;
  }, [trceForms]);

  const isTrceAiFeatureAllowed = useCallback((featureKey: string) => {
    return isAiFeatureAllowed(trceAiContext, featureKey);
  }, [trceAiContext]);

  /**
   * Load runtime config for the active country (TRCE first, legacy bootstrap fallback).
   * Captures: currency, language, modules, branding, cities
   */
  useEffect(() => {
    setModulesLoaded(false);
    setTrceForms(null);
    setTrceAiContext(null);
    setTrceDashboard(null);
    setTrcePromotions(null);
    setTrceExperiments(null);
    setTrceRecommendations(null);
    setTrceWhiteLabel(null);
    let cancelled = false;

    fetchCountryRuntime(API_BASE, selectedCountryCode)
      .then(({ data, themePack, forms, aiContext, dashboard, promotions, experiments, recommendations, whiteLabel }) => {
        if (cancelled) return;
        applyCountryRuntimePayload(data, selectedCountryCode, {
          setHomepageDisplayMode,
          setCountryFlag,
          setCountryName,
          setCurrencyCode,
          setCurrencySymbol,
          setCountryBranding,
          setSupportedLanguages,
          setLanguageState,
          setModules: (v) => setModules(v as AppModule[]),
          setPaymentMethods: (v) => setPaymentMethods(v as CountryPaymentMethod[]),
          setModuleFeatureFlags,
          setCountryCities: (v) => setCountryCities(v as CountryCity[]),
          setCity,
          setSelectedCityId,
        }, initialLangSet);
        setTrceForms(forms);
        setTrceAiContext(aiContext);
        setTrceDashboard(dashboard);
        setTrcePromotions(promotions);
        setTrceExperiments(experiments);
        setTrceRecommendations(recommendations);
        setTrceWhiteLabel(whiteLabel);
        if (themePack) applyTrceThemePack(themePack);
        if (whiteLabel) applyWhiteLabelDocumentBranding(whiteLabel);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setModulesLoaded(true);
      });

    // ── Fetch country-specific cities (fallback if not in TRCE/bootstrap) ──
    fetch(`${API_BASE}/countries/${selectedCountryCode}/cities`)
      .then(r => r.json())
      .then((data: any) => {
        const cities: any[] = Array.isArray(data) ? data : (data?.cities ?? []);
        if (cities.length > 0) {
          const active = cities.filter((c: any) => c.isActive !== false);
          setCountryCities(active);
          const first = active[0];
          setCity(first?.nameEn ?? first?.name_en ?? '');
          setSelectedCityId(first?.id ?? null);
        } else {
          setCountryCities([]);
          setCity('');
          setSelectedCityId(null);
        }
      })
      .catch(() => { setCountryCities([]); setCity(''); setSelectedCityId(null); });
    // Reset zone when country changes
    setCountryZones([]);
    setSelectedZoneId(null);
    return () => { cancelled = true; };
  }, [selectedCountryCode]);

  // ── Fetch zones when selectedCityId changes ─────────────────────────────
  useEffect(() => {
    if (!selectedCityId) { setCountryZones([]); return; }
    fetch(`${API_BASE}/zones/active?cityId=${selectedCityId}`)
      .then(r => r.json())
      .then((zones: any[]) => {
        const active = Array.isArray(zones) ? zones.filter(z => z.isActive !== false) : [];
        setCountryZones(active);
        setSelectedZoneId(active.length > 0 ? active[0].id : null);
      })
      .catch(() => { setCountryZones([]); setSelectedZoneId(null); });
  }, [selectedCityId]);

  /** Fetch available countries for ALL users (public endpoint) */
  const refreshCountries = React.useCallback(() => {
    fetch(`${API_BASE}/countries`)
      .then(r => r.json())
      .then((data: any) => {
        if (data?.countries) {
          setAvailableCountries(
            data.countries
              .filter((c: any) => c.status === 'active' || isGeneralAdmin || c.id === adminCountryId)
              .map((c: any) => ({
                id: c.id,
                code: c.code,
                nameDari: c.nameDari ?? c.name ?? c.code,
                nameEn: c.nameEn ?? c.name ?? c.code,
                flagIcon: c.flagIcon ?? '🏳',
                status: c.status ?? 'active',
                defaultCurrency: c.defaultCurrency,
                currencySymbol: c.currencySymbol,
              }))
          );
        }
      })
      .catch(() => {});
  }, [isGeneralAdmin, adminCountryId]);

  useEffect(() => { refreshCountries(); }, [refreshCountries]);

  /** Rebuild workspaces whenever the selected country changes */
  useEffect(() => {
    const rebuilt = buildDefaultWorkspaces(selectedCountryCode);
    setWorkspaces(rebuilt);
    setActiveWorkspaceState(prev => {
      if (!prev) return rebuilt.find(w => w.id === 'ws-customer') ?? null;
      const found = rebuilt.find(w => w.id === prev.id);
      return found ?? rebuilt.find(w => w.id === 'ws-customer') ?? null;
    });
  }, [selectedCountryCode]);

  /** Country switcher — available to all users */
  const setSelectedCountryCode = useCallback((code: string) => {
    setSelectedCountryCodeState(code);
    try {
      localStorage.setItem('tasallot_country_code', code);
    } catch {
      /* storage blocked */
    }
  }, []);

  /* ── WORKSPACE ACTIONS ── */
  const setActiveWorkspace = useCallback((ws: Workspace) => {
    setActiveWorkspaceState(ws);
    setWorkspaces(prev => prev.map(w =>
      w.id === ws.id ? { ...w, lastUsed: new Date().toISOString() } : w
    ));
    // Sync legacy role for backwards compatibility
    const roleMap: Record<WorkspaceModuleType, Role> = {
      customer: 'customer',
      vendor: 'vendor',
      mart_vendor: 'vendor',
      mart_operator: 'vendor',
      courier: 'courier',
      agent: 'agent',
      vehicle_rental: 'vehicle_company',
      real_estate: 'vendor',
      admin: 'admin',
      warehouse: 'vendor',
    };
    setRole(roleMap[ws.moduleType] ?? 'customer');
  }, []);

  const toggleWorkspaceFavorite = useCallback((wsId: string) => {
    setWorkspaces(prev => prev.map(w =>
      w.id === wsId ? { ...w, isFavorite: !w.isFavorite } : w
    ));
  }, []);

  const requestWorkspaceAccess = useCallback((moduleType: WorkspaceModuleType) => {
    // Demo mode: instantly approve access requests
    setWorkspaces(prev => prev.map(w =>
      w.moduleType === moduleType && (w.status === 'apply' || w.status === 'pending')
        ? { ...w, status: 'active', lastUsed: new Date().toISOString() }
        : w
    ));
  }, []);

  const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';
  const DEMO_VISIBLE_MODULES = ['classifieds', 'travel', 'kar', 'hotels', 'payments'];

  const isModuleVisible = (name: string): boolean => {
    if (DEMO_MODE) return DEMO_VISIBLE_MODULES.includes(name);
    const country = selectedCountryCode ?? "AF";
    if (!isWave1SurfaceAllowed(name, country, role)) return false;

    const customerLike = !role || role === "customer" || role === "user";
    if (!customerLike) {
      // Provider/internal: launch contract already authorized surface access
      return true;
    }

    // Customer discovery: WAVE1_PUBLIC only + country module row
    if (!isWave1CustomerNavVisible(name, country)) return false;
    if (!modulesLoaded) return false;
    if (modules.length === 0) return isWave1CustomerNavVisible(name, country);
    const mod = modules.find(m => m.name === name);
    if (!mod) {
      // WAVE1-004R9-RM1: CAP-PICK customer Web may appear before country_modules seed.
      if (name === "pick" && isWave1CustomerNavVisible(name, country)) return true;
      return false;
    }
    if (mod.status === 'hidden' || mod.status === 'paused') return false;
    // WAVE1_PUBLIC classification supersedes legacy internal_ready hide for AF launch candidates
    if (mod.status === 'internal_ready' && !isWave1CustomerNavVisible(name, country)) return false;
    return true;
  };

  const isModuleInNav = (name: string): boolean => {
    if (DEMO_MODE) return DEMO_VISIBLE_MODULES.includes(name);
    if (!isWave1CustomerNavVisible(name, selectedCountryCode ?? "AF")) return false;
    if (!modulesLoaded || modules.length === 0) return false;
    const mod = modules.find(m => m.name === name);
    if (!mod) return false;
    return mod.status !== 'hidden' && mod.status !== 'paused' && mod.isVisibleInNav;
  };

  const getModuleName = (mod: AppModule): string => {
    if (language === 'english') return mod.displayName;
    if (language === 'arabic')  return ARABIC_MODULE_NAMES[mod.name] ?? mod.displayName;
    return mod.displayNameDari;
  };

  const isEn = language === 'english';
  const isAr = language === 'arabic';

  const t = (labels: { dari: string; en: string; ar?: string; ps?: string; uz?: string }): string => {
    if (language === 'english') return labels.en;
    if (language === 'arabic')  return labels.ar ?? labels.en;
    if (language === 'pashto')  return labels.ps ?? labels.dari;
    if (language === 'uzbek')   return labels.uz ?? labels.en;
    return labels.dari;
  };

  /** Country-aware, language-aware price formatter (currencyOverride optional) */
  const formatPrice = (amount: number | string | null | undefined, currencyOverride?: string): string =>
    fmtPrice(amount, currencyOverride ?? currencyCode, language);

  /**
   * Checks if a module is available at the requested geographic depth.
   * Falls back to true (available) on network error to avoid false blocks.
   */
  const checkModuleAvailable = async (moduleKey: string, cityId?: number, zoneId?: number): Promise<boolean> => {
    try {
      let url = `${API_BASE}/module-guard/${selectedCountryCode}/${moduleKey}`;
      const params = new URLSearchParams();
      if (cityId) params.set("cityId", String(cityId));
      if (zoneId) params.set("zoneId", String(zoneId));
      if (params.toString()) url += `?${params.toString()}`;
      const r = await fetch(url);
      if (!r.ok) return false;
      const data = await r.json();
      return !!data.allowed;
    } catch {
      // WAVE1-002: fail closed — client must not override server deny
      return false;
    }
  };

  const phonePrefix = PHONE_PREFIXES[selectedCountryCode] ?? '+93';

  return (
    <AppContext.Provider value={{
      role, language, city, setRole, setLanguage, setCity,
      vehicleCompanyType, vehicleCompanyStatus, setVehicleCompanyType, setVehicleCompanyStatus,
      cartCount, setCartCount,
      cart, setCart,
      modules, modulesLoaded,
      isModuleVisible, isModuleInNav, getModuleName,
      isEn, isAr, isRtl, t,
      homepageDisplayMode,
      supportedLanguages,
      countryCities,
      selectedCityId, setSelectedCityId,
      countryZones,
      selectedZoneId, setSelectedZoneId,
      checkModuleAvailable,
      deploymentCountryCode: DEPLOYMENT_COUNTRY,
      selectedCountryCode, setSelectedCountryCode,
      availableCountries,
      refreshCountries,
      isGeneralAdmin,
      adminCountryId,
      adminTier,
      adminScopeValue,
      isLoggedIn,
      currentUserId,
      token: isLoggedIn ? (localStorage.getItem('tasallot_token') ?? null) : null,
      user: (() => { const id = currentUserId; return id ? { id } : null; })(),
      countryBranding,
      currencyCode,
      currencySymbol,
      formatPrice,
      measurementUnit,
      phonePrefix,
      countryFlag,
      countryName,
      paymentMethods,
      moduleFeatureFlags,
      trceForms,
      getTrceForm,
      trceAiContext,
      trceDashboard,
      trcePromotions,
      trceExperiments,
      trceRecommendations,
      trceWhiteLabel,
      isTrceAiFeatureAllowed,
      isDarkMode,
      themePreference,
      setThemePreference,
      toggleDarkMode,
      workspaces,
      activeWorkspace,
      setActiveWorkspace,
      toggleWorkspaceFavorite,
      requestWorkspaceAccess,
      savedAddresses,
      setSavedAddresses,
      userProfile,
      setUserProfile,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
